import React from 'react';
import { Controller } from 'react-hook-form';
import { Grid, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { ErrorMessage } from '@/components/common';
import { LANGUAGE_OPTIONS, DIFFICULTY_OPTIONS } from '@/constants/trainings';

/**
 * 기본 정보 입력 폼 컴포넌트
 * 
 * @param {Object} props
 * @param {Object} props.control - react-hook-form의 control 객체
 * @param {Object} props.errors - react-hook-form의 errors 객체
 * @param {Array} props.categories - 카테고리 목록
 * @param {Array} props.subCategories - 서브카테고리 목록
 * @param {number|null} props.selectedCategoryId - 선택된 카테고리 ID
 * @param {Function} props.onCategoryChange - 카테고리 변경 핸들러
 * @param {Function} props.onSubCategoryChange - 서브카테고리 변경 핸들러
 * @param {Function} props.onLanguageChange - 언어 변경 핸들러
 */
const TrainingBasicInfoForm = ({
  control,
  errors,
  categories,
  subCategories,
  selectedCategoryId,
  onCategoryChange,
  onSubCategoryChange,
  onLanguageChange,
}) => {
  // 공통 Select 필드 렌더링 헬퍼
  const renderSelectField = ({
    name,
    label,
    requiredMessage,
    errorKey,
    options,
    getOptionValue,
    getOptionLabel,
    disabled = false,
    customOnChange,
  }) => (
    <Grid item xs={3} sm={3}>
      <Controller
        control={control}
        name={name}
        rules={{ required: requiredMessage }}
        render={({ field }) => (
          <FormControl fullWidth required error={!!errors[errorKey]} disabled={disabled}>
            <InputLabel>{label}</InputLabel>
            <Select
              {...field}
              label={label}
              onChange={(e) => {
                field.onChange(e);
                if (customOnChange) {
                  customOnChange(e.target.value);
                }
              }}
            >
              {options.map((option) => (
                <MenuItem key={getOptionValue(option)} value={getOptionValue(option)}>
                  {getOptionLabel(option)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />
      <ErrorMessage errors={errors} name={name} />
    </Grid>
  );

  return (
    <>
      {/* 보안약점유형 */}
      {renderSelectField({
        name: 'categoryId',
        label: '보안약점유형',
        requiredMessage: '보안약점유형을 선택해주세요.',
        errorKey: 'categoryId',
        options: categories,
        getOptionValue: (category) => category.mainCategoryId,
        getOptionLabel: (category) => category.mainCategoryName,
        customOnChange: onCategoryChange,
      })}

      {/* 보안약점 */}
      {renderSelectField({
        name: 'subCategoryId',
        label: '보안약점',
        requiredMessage: '보안약점을 선택해주세요.',
        errorKey: 'subCategoryId',
        options: subCategories,
        getOptionValue: (sub) => sub.id,
        getOptionLabel: (sub) => sub.name,
        disabled: !selectedCategoryId,
        customOnChange: onSubCategoryChange,
      })}

      {/* 난이도 */}
      {renderSelectField({
        name: 'difficulty',
        label: '난이도',
        requiredMessage: '난이도를 선택해주세요.',
        errorKey: 'difficulty',
        options: DIFFICULTY_OPTIONS,
        getOptionValue: (option) => option.value,
        getOptionLabel: (option) => option.label,
      })}

      {/* 언어 */}
      {renderSelectField({
        name: 'languageId',
        label: '언어',
        requiredMessage: '언어를 선택해주세요.',
        errorKey: 'languageId',
        options: LANGUAGE_OPTIONS,
        getOptionValue: (lang) => lang.languageId,
        getOptionLabel: (lang) => lang.languageName,
        customOnChange: onLanguageChange,
      })}
    </>
  );
};

export default TrainingBasicInfoForm;
